import React, { useMemo } from 'react';
import { CheckIcon } from '@heroicons/react/solid';

export type PlatformLabel = 'YouTube' | 'Instagram' | 'TikTok' | 'Twitch';
/** @deprecated Use `PlatformLabel`; kept as alias to avoid cascade rename. */
export type Platform = PlatformLabel;

export interface PlatformOption {
  id: string;
  name: string;
  icon: 'youtube' | 'instagram' | 'tiktok' | 'twitch';
  color: string;
  available?: boolean;
}

interface PlatformMultiSelectProps {
  options?: PlatformOption[];
  selected: PlatformLabel[];
  onChange: (selected: PlatformLabel[]) => void;
  showComingSoon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  variant?: 'default' | 'pills';
  showGradient?: boolean;
}

export type PlatformIconId = 'youtube' | 'instagram' | 'tiktok' | 'twitch';

// Platform icon components using simple SVG paths
const PlatformIcons: Record<PlatformIconId, React.FC<{ className?: string; color?: string }>> = {
  youtube: ({ className, color }) => (
    <svg className={className} viewBox="0 0 24 24" fill={color || 'currentColor'}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  instagram: ({ className, color }) => (
    <svg className={className} viewBox="0 0 24 24" fill={color || 'currentColor'}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  tiktok: ({ className, color }) => (
    <svg className={className} viewBox="0 0 24 24" fill={color || 'currentColor'}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  ),
  twitch: ({ className, color }) => (
    <svg className={className} viewBox="0 0 24 24" fill={color || 'currentColor'}>
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  ),
};

/** Maps API / stored platform strings (e.g. youtube, Instagram) to icon id. */
export function apiPlatformToIconId(platform: string | undefined | null): PlatformIconId {
  const p = (platform || 'youtube').trim().toLowerCase();
  if (p === 'instagram' || p === 'ig') return 'instagram';
  if (p === 'tiktok') return 'tiktok';
  if (p === 'twitch') return 'twitch';
  if (p === 'youtube' || p === 'yt') return 'youtube';
  return 'youtube';
}

const platformAccessibilityLabels: Record<PlatformIconId, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  twitch: 'Twitch',
};

// Platform colors for various states
export const platformColorValues = {
  youtube: '#FF0000',
  instagram: '#E1306C', // More pink/magenta
  tiktok: '#000000',
  twitch: '#9146FF',
};

/** Single-platform glyph for creator rows, lists, etc. */
export const CreatorPlatformIcon: React.FC<{
  platform?: string | null;
  className?: string;
  iconClassName?: string;
}> = ({ platform, className = '', iconClassName = 'h-3.5 w-3.5' }) => {
  const id = apiPlatformToIconId(platform);
  const Icon = PlatformIcons[id];
  const color = platformColorValues[id];
  const label = platformAccessibilityLabels[id];
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${className}`}
      title={label}
      aria-label={label}
      role="img"
    >
      <Icon className={iconClassName} color={color} />
    </span>
  );
};

const platformColors = {
  youtube: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600' },
  instagram: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-600' },
  tiktok: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' },
  twitch: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600' },
};

const sizeClasses = {
  sm: { chip: 'px-2.5 py-1.5 text-xs gap-1.5', icon: 'h-3.5 w-3.5', check: 'h-3 w-3' },
  md: { chip: 'px-3 py-2 text-sm gap-2', icon: 'h-4 w-4', check: 'h-3.5 w-3.5' },
  lg: { chip: 'px-4 py-2.5 text-sm gap-2.5', icon: 'h-5 w-5', check: 'h-4 w-4' },
};

// Helper to generate CSS gradient from selected platforms
export const getSelectedPlatformGradient = (selectedPlatforms: PlatformLabel[], direction: string = '135deg'): string => {
  const colors: string[] = [];
  
  if (selectedPlatforms.includes('YouTube')) colors.push(platformColorValues.youtube);
  if (selectedPlatforms.includes('Instagram')) colors.push(platformColorValues.instagram);
  if (selectedPlatforms.includes('TikTok')) colors.push(platformColorValues.tiktok);
  if (selectedPlatforms.includes('Twitch')) colors.push(platformColorValues.twitch);
  
  if (colors.length === 0) return 'linear-gradient(90deg, #64748b, #94a3b8)'; // slate fallback
  if (colors.length === 1) return colors[0];
  
  // Create gradient with color stops
  const stops = colors.map((color, idx) => {
    const percent = (idx / (colors.length - 1)) * 100;
    return `${color} ${percent}%`;
  }).join(', ');
  
  return `linear-gradient(${direction}, ${stops})`;
};

// Reusable gradient bar component based on selected platforms
interface PlatformGradientBarProps {
  selectedPlatforms: PlatformLabel[];
  height?: string;
  className?: string;
  direction?: string;
}

export const PlatformGradientBar: React.FC<PlatformGradientBarProps> = ({
  selectedPlatforms,
  height = '4px',
  className = '',
  direction = '90deg',
}) => {
  const gradient = getSelectedPlatformGradient(selectedPlatforms, direction);
  
  return (
    <div 
      className={`w-full rounded-full ${className}`}
      style={{ 
        background: gradient,
        height,
      }}
    />
  );
};

export const PlatformMultiSelect: React.FC<PlatformMultiSelectProps> = ({
  options = defaultPlatformOptions,
  selected,
  onChange,
  showComingSoon = true,
  size = 'md',
  className = '',
  variant = 'default',
  showGradient = false,
}) => {
  const togglePlatform = (platformId: string) => {
    const option = options.find(o => o.id === platformId || o.name === platformId);
    if (option && option.available === false) return;

    const platformName = option?.name as PlatformLabel;
    if (!platformName) return;

    if (selected.includes(platformName)) {
      onChange(selected.filter(p => p !== platformName));
    } else {
      onChange([...selected, platformName]);
    }
  };

  // Generate gradient background style
  const gradientStyle = useMemo(() => {
    if (!showGradient) return {};
    const gradient = getSelectedPlatformGradient(selected);
    return { background: gradient };
  }, [selected, showGradient]);

  const sizes = sizeClasses[size];
  const isPills = variant === 'pills';

  return (
    <div className="relative">
      {/* Gradient indicator bar (shown when showGradient is true and multiple platforms selected) */}
      {showGradient && selected.length > 1 && (
        <div 
          className="absolute -top-1 left-0 right-0 h-1 rounded-full"
          style={gradientStyle}
        />
      )}
      
      <div className={`flex flex-wrap gap-2 ${isPills ? 'bg-white rounded-xl border border-border p-1.5 shadow-sm' : ''} ${className}`}>
        {options.map((option) => {
          const isSelected = selected.some(s => s === option.id || s === option.name);
          const isAvailable = option.available !== false;
          const Icon = PlatformIcons[option.icon];
          const colors = platformColors[option.icon];

          return (
            <button
              type="button"
              key={option.id}
              onClick={() => togglePlatform(option.id)}
              disabled={!isAvailable}
              className={`
                inline-flex items-center ${sizes.chip} ${isPills ? 'rounded-lg' : 'rounded-chip'} font-medium
                transition-all duration-200 border-2
                ${isSelected
                  ? `${colors.bg} ${colors.border} ${colors.text} shadow-sm`
                  : isAvailable
                    ? `${colors.bg} ${colors.text} ${colors.border} hover:shadow-sm`
                    : 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                }
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bearriBlue
              `}
              aria-pressed={isSelected}
            >
              {Icon && (
                <Icon
                  className={sizes.icon}
                  color={option.color}
                />
              )}
              <span>{option.name}</span>
              
              {/* pills show selection by fill + hairline (researchWorkbench.css) */}
              {isSelected && !isPills && (
                <CheckIcon className={`${sizes.check} ml-0.5`} />
              )}
              
              {!isAvailable && showComingSoon && (
                <span className="ml-1 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Default platform options for convenience
// Note: Set available: true for all to enable testing gradient effects
export const defaultPlatformOptions: PlatformOption[] = [
  { id: 'YouTube', name: 'YouTube', icon: 'youtube', color: '#FF0000', available: true },
  { id: 'Instagram', name: 'Instagram', icon: 'instagram', color: '#E1306C', available: true },
  { id: 'TikTok', name: 'TikTok', icon: 'tiktok', color: '#000000', available: true },
  { id: 'Twitch', name: 'Twitch', icon: 'twitch', color: '#9146FF', available: true },
];
